<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-1">차트 영역 강조</h5>
        <small class="text-muted">
          영역 박스 / 기준선 / 점을 추가해 차트의 특정 부분을 강조할 수 있습니다. (pie / doughnut /
          radar 등 원형 차트에서는 적용되지 않습니다.)
        </small>
      </div>
      <b-button size="sm" variant="primary" @click="addAnnotation">+ 강조 항목 추가</b-button>
    </div>

    <div v-if="!editableAnnotations.length" class="text-center text-muted border rounded p-4">
      아직 추가된 강조 항목이 없습니다.
    </div>

    <div
      v-for="(ann, ann_idx) in editableAnnotations"
      :key="ann.id || `annotation-${ann_idx}`"
      class="border rounded p-3 mb-3"
    >
      <b-row>
        <b-col cols="12" md="3">
          <b-form-group :label-for="`ann-${ann_idx}-type`" label="종류">
            <b-form-select
              :id="`ann-${ann_idx}-type`"
              v-model="ann.type"
              :options="annotation_type_options"
              @change="emitChange"
            />
          </b-form-group>
        </b-col>

        <template v-if="ann.type === 'box'">
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-axis`" label="기준 축">
              <b-form-select
                :id="`ann-${ann_idx}-axis`"
                v-model="ann.axis"
                :options="annotation_axis_options"
                @change="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-min`" label="최소값(min)">
              <b-form-input
                :id="`ann-${ann_idx}-min`"
                v-model="ann.min"
                placeholder="숫자 또는 카테고리 라벨"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-max`" label="최대값(max)">
              <b-form-input
                :id="`ann-${ann_idx}-max`"
                v-model="ann.max"
                placeholder="숫자 또는 카테고리 라벨"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
        </template>

        <template v-else-if="ann.type === 'line'">
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-line-axis`" label="기준 축">
              <b-form-select
                :id="`ann-${ann_idx}-line-axis`"
                v-model="ann.axis"
                :options="annotation_axis_options"
                @change="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group :label-for="`ann-${ann_idx}-value`" label="기준선 값">
              <b-form-input
                :id="`ann-${ann_idx}-value`"
                v-model="ann.value"
                placeholder="기준선을 그릴 값"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
        </template>

        <template v-else-if="ann.type === 'point'">
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-xValue`" label="X 값">
              <b-form-input
                :id="`ann-${ann_idx}-xValue`"
                v-model="ann.xValue"
                placeholder="X축 값 또는 라벨"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-yValue`" label="Y 값">
              <b-form-input
                :id="`ann-${ann_idx}-yValue`"
                v-model="ann.yValue"
                placeholder="Y축 값"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3">
            <b-form-group :label-for="`ann-${ann_idx}-pointRadius`" label="점 크기(px)">
              <b-form-input
                :id="`ann-${ann_idx}-pointRadius`"
                v-model="ann.pointRadius"
                type="number"
                min="1"
                max="40"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
        </template>

        <b-col cols="12"><hr class="my-1" /></b-col>

        <b-col v-if="ann.type !== 'line'" cols="12" md="3">
          <b-form-group label="배경색">
            <b-dropdown
              size="lg"
              variant="link"
              toggle-class="text-decoration-none p-0"
              menu-class="p-0"
              no-caret
              block
              @hidden="onColorPickHidden(ann_idx, 'backgroundColor')"
            >
              <template #button-content>
                <div class="border border-light rounded">
                  <div
                    class="h6 rounded text-white px-3 py-2 mb-0"
                    :style="{ backgroundColor: convertingColor(ann.backgroundColor) }"
                  >
                    배경색
                  </div>
                </div>
              </template>
              <b-dropdown-form class="p-0" form-class="p-0">
                <chrome-picker v-model="ann.backgroundColor" />
              </b-dropdown-form>
            </b-dropdown>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="3">
          <b-form-group label="테두리/선 색">
            <b-dropdown
              size="lg"
              variant="link"
              toggle-class="text-decoration-none p-0"
              menu-class="p-0"
              no-caret
              block
              @hidden="onColorPickHidden(ann_idx, 'borderColor')"
            >
              <template #button-content>
                <div class="border border-light rounded">
                  <div
                    class="h6 rounded text-white px-3 py-2 mb-0"
                    :style="{ backgroundColor: convertingColor(ann.borderColor) }"
                  >
                    테두리/선 색
                  </div>
                </div>
              </template>
              <b-dropdown-form class="p-0" form-class="p-0">
                <chrome-picker v-model="ann.borderColor" />
              </b-dropdown-form>
            </b-dropdown>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="3">
          <b-form-group :label-for="`ann-${ann_idx}-borderWidth`" label="선 굵기">
            <b-form-input
              :id="`ann-${ann_idx}-borderWidth`"
              v-model="ann.borderWidth"
              type="number"
              min="0"
              max="20"
              @input="emitChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12"><hr class="my-1" /></b-col>

        <b-col cols="12">
          <b-form-checkbox v-model="ann.labelEnabled" @change="emitChange">
            라벨 표시
          </b-form-checkbox>
        </b-col>

        <template v-if="ann.labelEnabled">
          <b-col cols="12" md="4">
            <b-form-group :label-for="`ann-${ann_idx}-label`" label="라벨 텍스트">
              <b-form-input :id="`ann-${ann_idx}-label`" v-model="ann.label" @input="emitChange" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2">
            <b-form-group :label-for="`ann-${ann_idx}-labelPositionX`" label="가로 정렬">
              <b-form-select
                :id="`ann-${ann_idx}-labelPositionX`"
                v-model="ann.labelPositionX"
                :options="annotation_label_position_options"
                @change="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2">
            <b-form-group :label-for="`ann-${ann_idx}-labelPositionY`" label="세로 정렬">
              <b-form-select
                :id="`ann-${ann_idx}-labelPositionY`"
                v-model="ann.labelPositionY"
                :options="annotation_label_position_options"
                @change="emitChange"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2">
            <b-form-group label="글자색">
              <b-dropdown
                size="lg"
                variant="link"
                toggle-class="text-decoration-none p-0"
                menu-class="p-0"
                no-caret
                block
                @hidden="onColorPickHidden(ann_idx, 'labelColor')"
              >
                <template #button-content>
                  <div class="border border-light rounded">
                    <div
                      class="rounded px-2 py-1 mb-0"
                      :style="{
                        backgroundColor: convertingColor(ann.labelColor),
                        color: '#fff'
                      }"
                    >
                      글자
                    </div>
                  </div>
                </template>
                <b-dropdown-form class="p-0" form-class="p-0">
                  <chrome-picker v-model="ann.labelColor" />
                </b-dropdown-form>
              </b-dropdown>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2">
            <b-form-group :label-for="`ann-${ann_idx}-labelFontSize`" label="글자 크기">
              <b-form-input
                :id="`ann-${ann_idx}-labelFontSize`"
                v-model="ann.labelFontSize"
                type="number"
                min="8"
                max="40"
                @input="emitChange"
              />
            </b-form-group>
          </b-col>
        </template>

        <b-col cols="12" class="text-right mt-2">
          <b-button size="sm" variant="outline-danger" @click="removeAnnotation(ann_idx)">
            삭제
          </b-button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { Chrome } from 'vue-color';
import debounce from 'lodash.debounce';
import {
  annotation_type_options,
  annotation_axis_options,
  annotation_label_position_options,
  DEFAULT_ANNOTATION
} from '@/util/chart/annotation.utils.js';

export default {
  name: 'EditChartAnnotations',
  components: {
    'chrome-picker': Chrome
  },
  props: {
    options: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      annotation_type_options,
      annotation_axis_options,
      annotation_label_position_options,
      editableAnnotations: []
    };
  },
  watch: {
    options: {
      deep: true,
      handler(_updated) {
        this.editableAnnotations = this._pickAnnotations(_updated);
      }
    }
  },
  created() {
    this.editableAnnotations = this._pickAnnotations(this.options);
  },
  beforeDestroy() {
    // 모달이 빠르게 닫혔다 열리는 흐름에서 trailing emit 이 stale 인스턴스로 발사되는 것을 방지
    this.emitChange?.cancel?.();
  },
  methods: {
    _pickAnnotations(opts) {
      const _saved = opts?.plugins?.annotations;
      return Array.isArray(_saved) ? JSON.parse(JSON.stringify(_saved)) : [];
    },

    addAnnotation() {
      this.editableAnnotations.push({
        ...DEFAULT_ANNOTATION,
        id: `annotation_${Date.now()}_${this.editableAnnotations.length}`
      });
      this.emitChange();
    },

    removeAnnotation(idx) {
      this.editableAnnotations.splice(idx, 1);
      this.emitChange();
    },

    onColorPickHidden(idx, prop) {
      const _ann = this.editableAnnotations[idx];
      if (!_ann) return;
      this.$set(this.editableAnnotations[idx], prop, this.convertingColor(_ann[prop]));
      this.emitChange();
    },

    convertingColor(color) {
      if (typeof color === 'string') {
        return color;
      } else if (typeof color === 'object' && color && 'hex8' in color) {
        return color.hex8;
      } else if (typeof color === 'object' && color && 'hex' in color) {
        return color.hex;
      } else {
        return 'rgba(239, 239, 239, 1)';
      }
    },

    emitChange: debounce(function () {
      this.$emit('updateAnnotations', JSON.parse(JSON.stringify(this.editableAnnotations)));
    }, 300)
  }
};
</script>
